import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';

// ─── Productos (packshots reales, fondo transparente). Cada uno lleva una
//     escala relativa a su tamaño real: un cloro de litro es mucho más grande
//     que un jaboncito de 75 g. Van parados sobre el estante, así que la altura
//     distinta se lee como tamaño distinto. ───
import okiPanales from './venta-especial-assets/productos/oki-panales.webp';
import dalanShampoo from './venta-especial-assets/productos/dalan-shampoo.webp';
import premioDetergente from './venta-especial-assets/productos/premio-detergente.webp';
import okiBabyWashing from './venta-especial-assets/productos/oki-baby-washing.webp';
import adiJabon from './venta-especial-assets/productos/adi-jabon.webp';
import premioCloro from './venta-especial-assets/productos/premio-cloro.webp';
import okiCrema from './venta-especial-assets/productos/oki-crema.webp';
import premioLavaplatos from './venta-especial-assets/productos/premio-lavaplatos.webp';

// ─── Logos de marca a color (portafolio SURA) ───
import marcaDalan from './venta-especial-assets/marcas/dalan.png';
import marcaOki from './venta-especial-assets/marcas/oki.png';
import marcaVale from './venta-especial-assets/marcas/vale.png';
import marcaAdi from './venta-especial-assets/marcas/adi.png';
import marcaPremio from './venta-especial-assets/marcas/premio.png';
import marcaDiva from './venta-especial-assets/marcas/diva.png';
import marcaFresca from './venta-especial-assets/marcas/fresca.png';
import marcaTisuf from './venta-especial-assets/marcas/tisuf.png';
import marcaCovex from './venta-especial-assets/marcas/covex.png';

// Orden alternando alto/bajo para que la fila tenga ritmo visual.
const PRODUCTOS = [
  { src: okiPanales, escala: 0.92, alt: 'Pañales Oki' },        // pack voluminoso
  { src: adiJabon, escala: 0.5, alt: 'Jabón Adi' },             // barra 75 g (el más chico)
  { src: premioCloro, escala: 1.3, alt: 'Cloro Premio' },       // botella de litro: la más grande
  { src: okiCrema, escala: 0.56, alt: 'Crema Oki' },            // pote 160 g
  { src: dalanShampoo, escala: 0.9, alt: 'Shampoo Dalan' },     // botella 450 ml
  { src: premioLavaplatos, escala: 0.68, alt: 'Lavaplatos Premio' }, // pote 180 g
  { src: okiBabyWashing, escala: 0.86, alt: 'Baby washing Oki' },    // botella 500 ml
  { src: premioDetergente, escala: 0.88, alt: 'Detergente Premio' }, // pack 400 g
];

const MARCAS = [
  { src: marcaDalan, alt: 'Dalan' },
  { src: marcaOki, alt: 'Oki' },
  { src: marcaVale, alt: 'Vale' },
  { src: marcaAdi, alt: 'Adi' },
  { src: marcaPremio, alt: 'Premio' },
  { src: marcaDiva, alt: 'Diva' },
  { src: marcaFresca, alt: 'Fresca' },
  { src: marcaTisuf, alt: 'Tisuf' },
  { src: marcaCovex, alt: 'Covex' },
];

// Paleta anclada en el navy real de las marcas + un solo acento naranja.
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@600;700;800;900&display=swap');

  .vb-card {
    position: relative;
    background: #ffffff;
    border: 1px solid #E7EBF1;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(20,25,45,0.04), 0 10px 34px -14px rgba(20,25,45,0.14);
    animation: vb-enter 0.7s cubic-bezier(0.16,1,0.3,1) both;
  }

  .vb-inner {
    display: grid;
    grid-template-columns: minmax(220px, 358px) 1fr;
    grid-template-areas: "top belt" "marcas marcas";
    align-items: center;
    gap: 0 30px;
    padding: clamp(20px, 2.6vw, 34px);
  }

  .vb-top { grid-area: top; }
  .vb-beltwrap { grid-area: belt; align-self: center; min-width: 0; }

  /* ── Eyebrow con etiqueta sólida ── */
  .vb-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    color: #1E2A4A;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.19em;
    text-transform: uppercase;
  }
  .vb-tag {
    display: inline-block;
    padding: 3px 8px;
    background: #FF5A1F;
    color: #ffffff;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    border-radius: 5px;
  }

  /* ── Hero ── */
  .vb-hero {
    margin: 15px 0 0;
    font-family: 'Archivo', system-ui, sans-serif;
    font-weight: 900;
    font-size: clamp(1.9rem, 3.4vw, 2.9rem);
    line-height: 0.94;
    letter-spacing: -0.022em;
    color: #1E2A4A;
  }
  .vb-hero .vb-accent { color: #FF5A1F; }

  .vb-sub {
    margin: 12px 0 0;
    font-size: 15px;
    line-height: 1.45;
    font-weight: 500;
    color: #5A6478;
    max-width: 34ch;
  }

  /* ── Acciones ── */
  .vb-actions {
    margin-top: 22px;
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }
  .vb-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #FF5A1F;
    color: #ffffff;
    font-family: 'Archivo', system-ui, sans-serif;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: 0.01em;
    padding: 13px 22px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    box-shadow: 0 8px 18px -8px rgba(255,90,31,0.6);
    transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  }
  .vb-cta:hover {
    background: #E8480F;
    transform: translateY(-1px);
    box-shadow: 0 12px 22px -8px rgba(255,90,31,0.7);
  }
  .vb-cta:active { transform: translateY(0); }
  .vb-cta:focus-visible { outline: 2px solid #1E2A4A; outline-offset: 3px; }
  .vb-cta svg { transition: transform 0.25s ease; }
  .vb-cta:hover svg { transform: translateX(3px); }

  /* Reloj regresivo */
  .vb-countdown {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .vb-cd-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #C2400F;
  }
  .vb-cd-title svg { color: #FF5A1F; }
  .vb-cd-boxes { display: flex; gap: 6px; }
  .vb-cd-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 46px;
    padding: 7px 5px;
    background: #1E2A4A;
    border-radius: 9px;
  }
  .vb-cd-box b {
    font-family: 'Archivo', system-ui, sans-serif;
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
    color: #ffffff;
    font-variant-numeric: tabular-nums;
  }
  .vb-cd-box i {
    margin-top: 4px;
    font-style: normal;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #93A0B8;
  }

  /* ── La cinta de productos (flotan centrados sobre el blanco) ── */
  .vb-belt {
    position: relative;
    height: 216px;
    overflow: hidden;
    background: transparent;
  }
  .vb-track {
    --vb-h: 160px;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;         /* productos centrados verticalmente */
    gap: clamp(18px, 2.2vw, 32px);
    width: max-content;
    padding: 0 26px;
    animation: vb-roll 40s linear infinite;
    will-change: transform;
  }
  .vb-belt:hover .vb-track { animation-play-state: paused; }

  .vb-item {
    position: relative;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
  .vb-prod {
    display: block;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 9px 11px rgba(20,25,45,0.16));
    transition: transform 0.35s cubic-bezier(0.16,1,0.3,1);
  }
  .vb-item:hover .vb-prod { transform: translateY(-8px) scale(1.04); }

  /* Difuminado a los lados: los productos entran y salen suaves */
  .vb-fade {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 48px;
    pointer-events: none;
    z-index: 2;
  }
  .vb-fade-l { left: 0; background: linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0) 100%); }
  .vb-fade-r { right: 0; background: linear-gradient(270deg, #ffffff 0%, rgba(255,255,255,0) 100%); }

  /* ── Marcas: franja inferior de inicio a fin, pequeñas y a color ── */
  .vb-marcas {
    grid-area: marcas;
    margin-top: clamp(18px, 2vw, 26px);
    padding-top: clamp(16px, 1.8vw, 22px);
    border-top: 1px solid #EEF1F5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    flex-wrap: wrap;
  }
  .vb-marca {
    height: 20px;
    width: auto;
    object-fit: contain;
    flex-shrink: 0;
  }

  @keyframes vb-enter {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes vb-roll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }

  /* ── Responsive ── */
  @media (max-width: 767px) {
    .vb-inner {
      grid-template-columns: 1fr;
      grid-template-areas: "top" "belt" "marcas";
      gap: 4px 0;
    }
    .vb-beltwrap { margin-top: 16px; }
    .vb-belt { height: 186px; }
    .vb-track { --vb-h: 132px; }
    .vb-marcas { justify-content: center; gap: 16px 22px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .vb-card { animation: none; }
    .vb-track { animation: none; }
    .vb-cta, .vb-cta svg, .vb-prod { transition: none; }
  }
`;

// Tiempo restante hasta la fecha límite de pedido, desglosado para el reloj.
function calcRestante(data) {
  if (!data?.fechaLimitePedido) return null;
  const ms = new Date(data.fechaLimitePedido).getTime() - Date.now();
  if (isNaN(ms) || ms <= 0) return { expirado: true };
  return {
    expirado: false,
    d: Math.floor(ms / 86400000),
    h: Math.floor((ms % 86400000) / 3600000),
    m: Math.floor((ms % 3600000) / 60000),
    s: Math.floor((ms % 60000) / 1000),
  };
}

const VentaEspecialBanner = ({ data }) => {
  const navigate = useNavigate();

  // Reloj regresivo hasta el cierre del pedido. La ventana de compra es corta
  // (dos días), así que el countdown en vivo carga la urgencia real.
  const [tiempo, setTiempo] = useState(() => calcRestante(data));
  useEffect(() => {
    if (!data?.fechaLimitePedido) return undefined;
    const id = setInterval(() => setTiempo(calcRestante(data)), 1000);
    return () => clearInterval(id);
  }, [data]);

  if (!data) return null;

  // Duplicamos la lista para que el loop de la cinta no tenga costura.
  const cinta = [...PRODUCTOS, ...PRODUCTOS];

  return (
    <div className="vb-card">
      <style>{styles}</style>

      <div className="vb-inner">
        {/* Texto */}
        <div className="vb-top">
          <span className="vb-eyebrow">
            Venta empleado
            <span className="vb-tag">Activa</span>
          </span>
          <h2 className="vb-hero">
            Haz tu pedido <span className="vb-accent">ya.</span>
          </h2>
          <p className="vb-sub">Tus marcas, a precio de empleado.</p>

          <div className="vb-actions">
            <button className="vb-cta" onClick={() => navigate('/productos-sura')}>
              Hacer mi pedido
              <ArrowRight size={18} strokeWidth={2.5} />
            </button>
          </div>
          {tiempo && !tiempo.expirado && (
            <div className="vb-countdown">
              <span className="vb-cd-title">
                <Clock size={14} strokeWidth={2.2} /> Cierra en
              </span>
              <div className="vb-cd-boxes">
                <span className="vb-cd-box"><b>{String(tiempo.d).padStart(2, '0')}</b><i>días</i></span>
                <span className="vb-cd-box"><b>{String(tiempo.h).padStart(2, '0')}</b><i>hrs</i></span>
                <span className="vb-cd-box"><b>{String(tiempo.m).padStart(2, '0')}</b><i>min</i></span>
                <span className="vb-cd-box"><b>{String(tiempo.s).padStart(2, '0')}</b><i>seg</i></span>
              </div>
            </div>
          )}
        </div>

        {/* La cinta de productos */}
        <div className="vb-beltwrap">
          <div className="vb-belt">
            <div className="vb-fade vb-fade-l" aria-hidden="true" />
            <div className="vb-track">
              {cinta.map((p, i) => (
                <div className="vb-item" key={i}>
                  <img
                    className="vb-prod"
                    src={p.src}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                    style={{ height: `calc(var(--vb-h) * ${p.escala})` }}
                  />
                </div>
              ))}
            </div>
            <div className="vb-fade vb-fade-r" aria-hidden="true" />
          </div>
        </div>

        {/* Marcas: franja inferior de inicio a fin */}
        <div className="vb-marcas">
          {MARCAS.map((m) => (
            <img key={m.alt} className="vb-marca" src={m.src} alt={m.alt} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default VentaEspecialBanner;
